import { useLayoutEffect, useRef } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';
import { useSearchViewMemory } from './SearchViewMemoryContext';

export default function useSearchBridgeFocus() {
  const root = useRef(null);
  const location = useLocation();
  const navigationType = useNavigationType();
  const memory = useSearchViewMemory();
  const entries = memory.choose('bridge-focus', [], () => new Map());
  useLayoutEffect(() => {
    const saved = entries.get(location.key);
    if (navigationType !== 'POP' || !saved || !root.current) return;
    let stopped = false;
    let observer;
    let timer;
    const stop = () => {
      stopped = true;
      observer?.disconnect();
      clearTimeout(timer);
      for (const type of ['pointerdown', 'touchstart', 'keydown'])
        window.removeEventListener(type, stop, true);
    };
    const restore = () => {
      if (stopped) return;
      const target = Array.from(root.current.querySelectorAll('a[href]')).find(
        (link) =>
          link.getAttribute('href') === saved.href &&
          (link.getAttribute('aria-label') || link.textContent) === saved.label,
      );
      if (target && target.getClientRects().length && !target.closest('[hidden]')) {
        target.focus({ preventScroll: true });
        stop();
      }
    };
    for (const type of ['pointerdown', 'touchstart', 'keydown'])
      window.addEventListener(type, stop, true);
    observer = new MutationObserver(restore);
    observer.observe(root.current, { childList: true, subtree: true, attributes: true });
    timer = setTimeout(stop, 5000);
    restore();
    return stop;
  }, [entries, location.key, navigationType]);
  return {
    ref: root,
    onClickCapture(event) {
      const link = event.target.closest('a[href]');
      if (
        !link ||
        !root.current?.contains(link) ||
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
      )
        return;
      entries.set(location.key, {
        href: link.getAttribute('href'),
        label: link.getAttribute('aria-label') || link.textContent,
      });
      if (entries.size > 120) entries.delete(entries.keys().next().value);
    },
  };
}
